import { LocalDate } from "@js-joda/core";
import { TipStatus, type TipDetails } from "model/Tip";
import { fetchTipsByDateRange } from "services/api/company/tips.v2.service";

/**
 * Tips recorded on the company's own Tips page, as opposed to the tips that
 * arrive on POS orders. These are keyed to a calendar date the user picked
 * rather than derived from an order timestamp, so — unlike orders — they need
 * no business-date/overnight adjustment: a tip dated Aug 13 belongs to Aug 13.
 */
export interface CompanyTipTotal {
  /** ISO "YYYY-MM-DD" */
  date: string;
  amount: number;
}

/**
 * Collapses the raw tip records into one total per date.
 *
 * A date can legitimately hold several tip records, so amounts are summed
 * rather than the last one winning. Cancelled tips are dropped — they
 * represent money that was never actually taken.
 */
export const sumCompanyTipsByDate = (tips: TipDetails[]): CompanyTipTotal[] => {
  const byDate = new Map<string, number>();
  for (const tip of tips) {
    if (tip.status === TipStatus.Cancelled) continue;
    if (!tip.date) continue;
    const date = tip.date.slice(0, 10);
    byDate.set(date, (byDate.get(date) ?? 0) + (tip.amount ?? 0));
  }
  return Array.from(byDate.entries())
    .map(([date, amount]) => ({ date, amount }))
    .sort((a, b) => a.date.localeCompare(b.date));
};

/** Fetches company tips for a date range, summed to one entry per date. */
export const fetchCompanyTips = async (
  companyId: number,
  fromDate: LocalDate,
  toDate: LocalDate,
): Promise<CompanyTipTotal[]> => {
  const res = await fetchTipsByDateRange(
    companyId,
    fromDate.toString(),
    toDate.toString(),
  );
  return sumCompanyTipsByDate(res.data.companyTips ?? []);
};

export const getCompanyTipsTotal = (totals: CompanyTipTotal[]): number =>
  totals.reduce((sum, t) => sum + t.amount, 0);
